import { getUserProfileRepository } from "@/infrastructure/di";
import type {
  ChangePasswordRequest,
  EmailChangeRequest,
  UpdateUserPreferencesRequest,
  UpdateUserProfileRequest,
} from "@/domain/user";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { useCreateMutation } from "../createMutation";
import { CURRENT_USER, NO_ORGANIZATION, userCache } from "./cache";

const currentUser = userCache.at(NO_ORGANIZATION);

export const useUpdateProfile = () =>
  useCreateMutation({
    mutationFn: (data: UpdateUserProfileRequest) => getUserProfileRepository().updateProfile(data),
    cache: (c) =>
      c.patch(currentUser, { id: () => CURRENT_USER, fromResponse: (profile) => ({ profile }) }),
    errorLogMessage: "Failed to update profile:",
  });

export const useChangePassword = () =>
  useCreateMutation({
    mutationFn: (data: ChangePasswordRequest) => getUserProfileRepository().changePassword(data),
    cache: (c) => c.none(),
    errorLogMessage: "Failed to change password:",
  });

export const useUpdatePreferences = () =>
  useCreateMutation({
    mutationFn: (data: UpdateUserPreferencesRequest) =>
      getUserProfileRepository().updatePreferences(data),
    cache: (c) =>
      c.patch(currentUser, {
        id: () => CURRENT_USER,
        optimistic: (preferences) => ({ preferences }),
        fromResponse: (preferences) => ({ preferences }),
        scope: "preferences",
      }),
    errorLogMessage: "Failed to update preferences:",
  });

const reconcileUser = { orgId: NO_ORGANIZATION, entities: [InvalidationEntity.USER] };

export const useRequestEmailChange = () =>
  useCreateMutation({
    mutationFn: (data: EmailChangeRequest) => getUserProfileRepository().requestEmailChange(data),
    cache: (c) => c.invalidate(reconcileUser),
    errorLogMessage: "Failed to request the email change:",
  });

export const useCancelEmailChange = () =>
  useCreateMutation({
    mutationFn: () => getUserProfileRepository().cancelEmailChange(),
    cache: (c) => c.invalidate(reconcileUser),
    errorLogMessage: "Failed to cancel the email change:",
  });

export const useSetDefaultOrganization = () =>
  useCreateMutation({
    mutationFn: (organizationUuid: string) =>
      getUserProfileRepository().setDefaultOrganization(organizationUuid),
    cache: (c) =>
      c.invalidate({
        orgId: NO_ORGANIZATION,
        entities: [],
        keys: () => [queryKeys.organization.userOrganizations()],
      }),
    errorLogMessage: "Failed to set the default organization:",
  });
